import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { NoddiAsyncError } from 'noddi-types';
import { NoddiInfoBanner, NoddiLink } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { log } from 'noddi-web';
import { useEffect } from 'react';
import { ApiErrorMessageWithTrans } from '../../Error/ApiErrorMessageWithTrans';
import {
  getInitialAvailableSalesItemsErrorCode,
  isExpectedInitialAvailableSalesItemsOutcome,
  NO_SALES_ITEMS_UPCOMING_WHEEL_STORAGE_BOOKING
} from './initialAvailableSalesItemsCustomerError';

interface Props {
  /** Absent when the catalog answered successfully but returned no cars at all. */
  error?: NoddiAsyncError;
  carCount: number;
  addressId: number;
  licensePlateNumbers: string[];
}

/**
 * Expected catalog rejections use an info notice with recovery. Unexpected API failures
 * use error UI; a successful response missing the requested cars is still reported.
 */
export const NoAvailableServicesMessage = ({ error, carCount, addressId, licensePlateNumbers }: Props) => {
  const apiErrorCode = error ? getInitialAvailableSalesItemsErrorCode(error) : undefined;
  const plates = licensePlateNumbers.join(', ');

  useEffect(() => {
    if (!error) {
      log({
        value: 'Booking catalog returned no cars',
        severity: 'error',
        extra: { responseBody: { addressId, plates } }
      });
    }
  }, [error, addressId, plates]);

  if (error && !isExpectedInitialAvailableSalesItemsOutcome(error)) {
    return <ApiErrorMessageWithTrans error={error} />;
  }

  const isTireHotelCase = apiErrorCode === NO_SALES_ITEMS_UPCOMING_WHEEL_STORAGE_BOOKING;
  const isOneCar = carCount === 1;

  const description = isTireHotelCase
    ? isOneCar
      ? t`Your selected vehicle already has an upcoming tire hotel booking. Because of that, we can't show any services for this selection right now. If you have questions, please contact us — we're happy to help.`
      : t`One of your selected vehicles already has an upcoming tire hotel booking. Because of that, we can't show any services for this selection right now. If you have questions, please contact us — we're happy to help.`
    : isOneCar
      ? t`We couldn't find any services you can book for your selected vehicle and address. That may be because we don't offer what you need at this location, or your vehicle doesn't match our available services. Try another address or vehicle, or contact us and we'll help you.`
      : t`We couldn't find any services you can book for your selected vehicles and address. That may be because we don't offer what you need at this location, or your vehicles don't match our available services. Try another address, adjust your vehicle selection, or contact us and we'll help you.`;

  return (
    <NoddiInfoBanner
      variant='info'
      title={t`No services available`}
      texts={[{ value: description }]}
      // An upcoming tire hotel booking is not fixed by picking another address or car.
      actionNode={
        isTireHotelCase ? undefined : (
          <>
            <NoddiLink to={customerRoutes.newBookingAddress.getPath()} size='small' variant='outline'>
              <Trans>Change address</Trans>
            </NoddiLink>
            <NoddiLink to={customerRoutes.newBookingCars.getPath()} size='small' variant='outline'>
              {isOneCar ? <Trans>Change car</Trans> : <Trans>Change cars</Trans>}
            </NoddiLink>
          </>
        )
      }
    />
  );
};
